import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Wrench, Zap, Fuel, Key, BatteryCharging, Truck, Anchor, HelpCircle, Globe, Car, Users, Settings } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const LandingPage = () => {
  const navigate = useNavigate();
  const { t, language, setLanguage } = useLanguage();

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  ];

  const services = [
    { icon: <Wrench className="w-8 h-8" />, name: t('services.tireChange'), desc: t('services.tireChangeDesc') },
    { icon: <Zap className="w-8 h-8" />, name: t('services.jumpStart'), desc: t('services.jumpStartDesc') },
    { icon: <Fuel className="w-8 h-8" />, name: t('services.fuelDelivery'), desc: t('services.fuelDeliveryDesc') },
    { icon: <Key className="w-8 h-8" />, name: t('services.lockout'), desc: t('services.lockoutDesc') },
    { icon: <BatteryCharging className="w-8 h-8" />, name: t('services.evCharging'), desc: t('services.evChargingDesc') },
    { icon: <Truck className="w-8 h-8" />, name: t('services.towRecovery'), desc: t('services.towRecoveryDesc') },
    { icon: <Anchor className="w-8 h-8" />, name: t('services.marineHelp'), desc: t('services.marineHelpDesc') },
    { icon: <Settings className="w-8 h-8" />, name: t('services.mobileMechanic'), desc: t('services.mobileMechanicDesc') },
    { icon: <HelpCircle className="w-8 h-8" />, name: t('services.generalHelp'), desc: t('services.generalHelpDesc') },
  ];

  const travelServices = [
    { 
      icon: <Car className="w-8 h-8" />, 
      name: 'Hire a Driver (Your Car)', 
      desc: 'Need a trusted driver to take your car to a destination? Post time and route, choose a verified driver, and track the trip live.' 
    },
    { 
      icon: <Users className="w-8 h-8" />, 
      name: 'Share a Ride (Carpool)', 
      desc: 'Going somewhere? Post your route and split fuel/tolls with riders. Not a taxi — just fair cost-sharing.' 
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-yellow-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <nav className="relative z-10 px-3 sm:px-6 py-6 flex justify-between items-center max-w-7xl mx-auto">
          <div className="flex items-center space-x-2">
            <img 
              src="/zooka-logo-white.png" 
              alt="Zooka" 
              className="h-16 sm:h-24 w-auto"
            />
          </div>
          <div className="flex items-center space-x-1 sm:space-x-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="text-white hover:bg-white/10 px-2 sm:px-4"
                  data-testid="language-selector"
                >
                  <Globe className="w-4 h-4 sm:w-5 sm:h-5 sm:mr-2" />
                  <span className="hidden sm:inline">{languages.find(l => l.code === language)?.flag}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    data-testid={`lang-${lang.code}`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              data-testid="nav-signup-btn"
              variant="outline"
              className="bg-yellow-400 text-blue-700 border-yellow-400 hover:bg-yellow-300 font-semibold text-xs sm:text-sm px-3 sm:px-4 py-2"
              onClick={() => navigate('/auth')}
            >
              Sign Up
            </Button>
            <Button
              data-testid="nav-login-btn"
              className="bg-white text-blue-700 hover:bg-gray-100 font-semibold text-xs sm:text-sm px-3 sm:px-4 py-2"
              onClick={() => navigate('/auth')}
            >
              Log In
            </Button>
          </div>
        </nav>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 md:py-32">
          <div className="text-center">
            <h1 data-testid="hero-title" className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6">
              {t('hero.title')}
              <br />
              <span className="text-yellow-400">{t('hero.titleHighlight')}</span>
            </h1>
            <p data-testid="hero-subtitle" className="text-xl md:text-2xl text-white/90 mb-12 max-w-3xl mx-auto">
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                data-testid="get-help-btn"
                size="lg"
                className="bg-yellow-400 text-blue-700 hover:bg-yellow-300 text-lg px-8 py-6 rounded-full font-bold"
                onClick={() => navigate('/auth?role=driver')}
              >
                {t('hero.getHelp')}
              </Button>
              <Button
                data-testid="earn-money-btn"
                size="lg"
                variant="outline"
                className="bg-transparent text-white border-2 border-white hover:bg-white/10 text-lg px-8 py-6 rounded-full font-semibold"
                onClick={() => navigate('/auth?role=helper')}
              >
                {t('hero.earnMoney')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <div className="py-20 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{t('services.title')}</h2>
          <p className="text-xl text-gray-600">{t('services.subtitle')}</p>
        </div>

        {/* Roadside Assistance Services */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">Roadside Assistance</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                data-testid={`service-card-${index}`}
                className="service-card bg-white p-8 rounded-2xl shadow-lg border border-gray-100"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl flex items-center justify-center text-yellow-400 mb-4">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">{service.name}</h3>
                <p className="text-gray-600">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Travel & Rides Section */}
        <div className="border-t border-gray-200 pt-16">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Travel & Rides</h3>
            <p className="text-lg text-gray-600">Two flexible options for your trip — stay compliant, stay safe.</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {travelServices.map((service, index) => (
              <div
                key={index}
                data-testid={`travel-service-card-${index}`}
                className="service-card bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-2xl shadow-lg border-2 border-blue-200"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl flex items-center justify-center text-white mb-4">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{service.name}</h3>
                <p className="text-gray-700 leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>

          {/* Compliance Notice */}
          <div className="mt-12 p-6 bg-gray-100 rounded-xl max-w-4xl mx-auto">
            <h4 className="text-sm font-bold text-gray-900 mb-3">Important Compliance Information</h4>
            <div className="space-y-2 text-xs text-gray-700">
              <p>• Zooka is a platform, not a motor carrier or TNC.</p>
              <p>• Carpool is cost-sharing only; no driver profit.</p>
              <p>• Designated Drivers operate the customer's vehicle; customer insurance must allow permissive drivers.</p>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="bg-gray-50 py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{t('howItWorks.title')}</h2>
            <p className="text-xl text-gray-600">{t('howItWorks.subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-800 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6 shadow-lg">
                1
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">{t('howItWorks.step1Title')}</h3>
              <p className="text-gray-600">{t('howItWorks.step1Desc')}</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-yellow-500 text-blue-800 rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6 shadow-lg">
                2
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">{t('howItWorks.step2Title')}</h3>
              <p className="text-gray-600">{t('howItWorks.step2Desc')}</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-800 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6 shadow-lg">
                3
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">{t('howItWorks.step3Title')}</h3>
              <p className="text-gray-600">{t('howItWorks.step3Desc')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-400 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('cta.title')}
          </h2>
          <p className="text-xl text-white/90 mb-8">
            {t('cta.subtitle')}
          </p>
          <Button
            data-testid="cta-get-started-btn"
            size="lg"
            className="bg-yellow-400 text-blue-800 hover:bg-yellow-300 text-lg px-12 py-6 rounded-full font-bold shadow-xl"
            onClick={() => navigate('/auth')}
          >
            {t('cta.button')}
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center mb-8">
            <div className="flex items-center mb-4 md:mb-0">
              <span className="text-3xl font-black text-yellow-400">Z</span>
              <span className="text-xl font-semibold">ooka</span>
            </div>
            <div className="flex space-x-8">
              <button
                onClick={() => navigate('/privacy')}
                className="text-gray-400 hover:text-white transition-colors"
              >
                {t('footer.privacy')}
              </button>
              <button
                onClick={() => navigate('/terms')}
                className="text-gray-400 hover:text-white transition-colors"
              >
                {t('footer.terms')}
              </button>
            </div>
          </div>
          
          {/* Compliance Footer */}
          <div className="border-t border-gray-800 pt-8 mb-8">
            <div className="bg-gray-800 rounded-lg p-6">
              <h4 className="text-sm font-bold text-yellow-400 mb-3">Compliance & Legal Notice</h4>
              <div className="space-y-2 text-xs text-gray-400">
                <p>• Zooka is a platform connecting service providers and customers. We are not a motor carrier, transportation network company (TNC), or employer.</p>
                <p>• Carpool/Ride-sharing services are cost-sharing arrangements only. Drivers may only recover actual costs (fuel, tolls). No profit is permitted.</p>
                <p>• Designated Driver services involve drivers operating the customer's personal vehicle. Customer's insurance policy must allow permissive use by additional drivers.</p>
                <p>• All service providers are independent contractors. Users must comply with local laws and regulations.</p>
              </div>
            </div>
          </div>

          <div className="text-center border-t border-gray-800 pt-8">
            <p className="text-gray-400 mb-2">{t('footer.tagline')}</p>
            <p className="text-lg font-semibold text-yellow-400 mb-4">{t('footer.pioneering')}</p>
            <p className="text-gray-500 text-sm">{t('footer.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;